import React, { useState, useEffect, useMemo } from "react";
import { Modal, Card, Row, Col, Button, Tooltip } from "antd";
import { useLocation } from "react-router";
import { DownloadOutlined, StarOutlined, StarFilled } from "@ant-design/icons";
import { orderBy } from "lodash";
import { useDispatch, useSelector } from "react-redux";

import BannerTop from "../../components/BannerTop/BannerTop";
import Localize, { l } from "../../libs/localization";
import CompanyFilters from "../../components/CompanyFilters/CompanyFilters";
import FiltersSummary from "../../components/FiltersSummary/FiltersSummary";
import CompanySearchResults from "../../components/CompanySearchResults/CompanySearchResults";
import SearchManagement from "../../components/SearchManagement/SearchManagement";
import CreateTagModal from "../../components/CreateTagModal/CreateTagModal";
import TagOptions from "../../components/Tag/TagOptions";
import LimitedFeatureBanner from "../../design-library/LimitedFeatureBanner/LimitedFeatureBanner.component";
import ContactSalesModal from "../../design-library/ContactSalesModal/ContactSalesModal.component";
import ErrorModal from "../../components/ErrorModal";

import useCompanySearch from "../../hooks/useCompanySearch";
import useTags from "../../hooks/useTags";
import useTagAssociations from "../../hooks/useTagAssociations";
import usePermissions from "../../hooks/usePermissions";
import { getFeatures } from "../../libs/features";
import { getFromLocalStorage } from "../../libs/helpers/localstorage";
import { exportCompaniesAction } from "../../actions/company.actions";
import {
  deleteSearchAction,
  getSearchListAction,
} from "../../actions/search.action";
import { attachTagToCompanySearchApi, TAG_ENTITY } from "../../api/tag.api";
import getUserPlugins from "../../libs/getUserPlugins";
import getPluginConsumption from "../../libs/getPluginConsumption";
import { EXPORT_COMPANIES_PLUGIN } from "../../api/user.api";
import moment from "moment";
import { getConsumptionApi } from "../../api/ecommerce.api";

const { confirm } = Modal;

const CompanySearch = () => {
  const features = getFeatures();
  const permissions = usePermissions();
  const tenant = getFromLocalStorage("lefacWebTenant")?.data;
  const dispatch = useDispatch();

  const {
    dataForm,
    setDataForm,
    filters,
    activeFilters,
    setCity,
    results,
    loading,
    brandAdvertiserFilters,
    setBrandAdvertiserFilters,
    companyNameLabels,
    setCompanyNameLabels,
    pageSize,
    setPageSize,
    order,
    orderDir,
    handleOrder,
    pageNum,
    setPageNum,
    handlePageChange,
    getParsedDataForm,
    resetSearch,
  } = useCompanySearch();

  const user = useSelector((state) => state.auth.user);
  const searchList = useSelector((state) => state.search.searchList.data);
  const loadingExport = useSelector(
    (state) => state.company.exportList.loading,
  );

  const [tagMode, setTagMode] = useState(false);
  const [tagAddModal, setAddTagModal] = useState(false);
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [exportLimitedModal, setExportLimitedModal] = useState(false);
  const [selectedSearchElement, setSelectedSearchElement] = useState(null);

  const [consumption, setConsumption] = useState(null);
  const consumptionPlugin =
    user?.licence?.tenants[tenant]?.plugins?.["6538ca30695c4015d3daeb7c"];

  async function loadConsumption() {
    try {
      if ((permissions.ecommerce || !!consumptionPlugin) && !consumption) {
        let periodStartDate = null;
        if (consumptionPlugin) {
          const period = consumptionPlugin.data.period === "month" ? 1 : 12;
          const firstDate = consumptionPlugin.data.periodStartDateTime;
          periodStartDate = moment(firstDate);
          while (
            periodStartDate.isBefore(moment().subtract(period, "months"))
          ) {
            periodStartDate.add(period, "months");
          }
          periodStartDate = periodStartDate.valueOf();
        }
        const res = await getConsumptionApi({ periodStartDate });
        setConsumption(res.data?.consumption);
      }
    } catch (e) {}
  }

  useEffect(() => {
    loadConsumption();
  }, [permissions.ecommerce]);

  const { selectedTag, setSelectedTag, tags, addToTag, reloadTags } = useTags({
    entity: TAG_ENTITY.COMPANY,
  });

  const companyIds = useMemo(
    () => results?.companies?.data?.map((c) => c._id) || [],
    [results],
  );

  const {
    data: tagAssociations,
    loading: tagAssociationsLoading,
    reload: reloadAssociations,
  } = useTagAssociations({ entity: TAG_ENTITY.COMPANY, entityIds: companyIds });

  useEffect(() => {
    if (permissions.savedSearch) {
      dispatch(getSearchListAction({ params: "entity=company" }));
    }
  }, [permissions.savedSearch, dispatch]);

  const onExportCompanies = async () => {
    const dataParams = { ...filters };
    delete dataParams.limit;
    delete dataParams.pageNum;
    delete dataParams.sort_direction;
    delete dataParams.sort_key;
    if (user.group === "SUPER-ADMIN" || user.group === "ADMIN")
      dispatch(exportCompaniesAction({ data: dataParams }));
    else {
      let creditsLeft = 0;
      if (permissions.exportCompaniesV2) {
        const pluginName = "ExportCompaniesV2";
        const plugin = getUserPlugins(user).find((p) => p.name === pluginName);
        creditsLeft =
          (plugin?.data?.companyLimit || 0) -
          (await getPluginConsumption({ user, pluginName }))?.companyExport
            ?.count;
      } else {
        const creditData = Object.values(
          user?.licence?.tenants[tenant]?.plugins,
        )?.find((plugin) => plugin.name === EXPORT_COMPANIES_PLUGIN)?.data;
        creditsLeft =
          creditData?.contactLimit - parseInt(creditData?.exportedCount, 10);
      }

      const content =
        creditsLeft > 0
          ? `${l("TAG.EXPORT_MODAL_DESCRIPTION")} ${creditsLeft}. ${l(
              "TAG.EXPORT_CONTINUE",
            )}`
          : l("ERROR.ERROR_EXPORT_CONTACTS");

      return confirm({
        title: l("TAG.EXPORT_MODAL_TITLE"),
        content,
        okButtonProps: { disabled: creditsLeft <= 0 },
        cancelText: l("COMMON.CANCEL"),
        onOk() {
          dispatch(exportCompaniesAction({ data: dataParams }));
        },
      });
    }
  };

  const associateTagCompany = async () => {
    try {
      await addToTag(selectedCompanies);
      reloadAssociations();
      setTagMode(false);
      setSelectedTag("");
    } catch (error) {
      ErrorModal.show(error);
    }
  };

  const associateAll = async () => {
    try {
      if (selectedTag) {
        const params = getParsedDataForm();
        await attachTagToCompanySearchApi({ data: params, tagId: selectedTag });
        reloadAssociations();
        setTagMode(false);
        setSelectedTag("");
      }
    } catch (error) {
      ErrorModal.show(error);
    }
  };

  const orderLabel = (value, key) => (
    <Button
      type="link"
      className={`${
        order === value ? "selected" : ""
      } searchPage__orders-button`}
      onClick={() => handleOrder(value)}
    >
      {key}
      {order === value && (
        <span className="searchPage__orders-icon">
          {orderDir === "asc" ? "↑" : "↓"}
        </span>
      )}
    </Button>
  );

  return (
    <>
      <BannerTop small>
        <h1 className="mt-4 orange-title">{l("PAGES.COMPANIES.TITLE")}</h1>
      </BannerTop>
      <div className="searchPage__filters animation fadein-up slow">
        <Card
          bordered={false}
          style={{ borderRadius: "10px" }}
          className="companyPage__body-card companyPage__body-card--custom customCard"
        >
          <FiltersSummary
            activeFilters={activeFilters}
            setDataForm={setDataForm}
            setCity={setCity}
            setBrandAdvertiserFilters={setBrandAdvertiserFilters}
            matchCount={results?.companies?.totalResults}
            companyNames={companyNameLabels}
          />
          <CompanyFilters
            dataForm={dataForm}
            setDataForm={setDataForm}
            onCompanyNameLabelsChange={setCompanyNameLabels}
            features={features}
            permissions={permissions}
            tenant={tenant}
            loadingSearch={loading}
            brandAdvertiserFilters={brandAdvertiserFilters}
            setBrandAdvertiserFilters={setBrandAdvertiserFilters}
            renderExtraActions={() => (
              <Row justify="end" gutter={8} align="middle">
                {permissions.savedSearch && (
                  <Col>
                    <SearchManagement
                      type="company"
                      searchList={orderBy(searchList, "created_at", "desc")}
                      value={selectedSearchElement}
                      onDeleteSearch={() => {
                        dispatch(
                          deleteSearchAction({
                            id: selectedSearchElement,
                            type: "company",
                          }),
                        );
                        setSelectedSearchElement(null);
                      }}
                      onSearchElementChange={(id, data) => {
                        setSelectedSearchElement(id);
                        setDataForm(data || {});
                      }}
                    />
                  </Col>
                )}
                <Col>
                  <Button
                    type="default"
                    onClick={() => {
                      setSelectedSearchElement(null);
                      resetSearch();
                    }}
                  >
                    {l("COMMON.RESET")}
                  </Button>
                </Col>
              </Row>
            )}
          />
        </Card>
      </div>

      <CompanySearchResults
        results={results}
        loading={loading}
        pageSize={pageSize}
        setPageSize={setPageSize}
        pageNum={pageNum}
        setPageNum={setPageNum}
        filters={filters}
        tagMode={tagMode}
        setTagMode={setTagMode}
        selectedTag={selectedTag}
        setSelectedTag={setSelectedTag}
        tags={tags}
        setSelectedCompanies={setSelectedCompanies}
        onAssociateClick={associateTagCompany}
        onAssociateAllClick={associateAll}
        onAddTagClick={() => setAddTagModal(true)}
        tagAssociationsLoading={tagAssociationsLoading}
        tagAssociations={tagAssociations}
        permissions={permissions}
        onExportClick={onExportCompanies}
        loadingExport={loadingExport}
        onPageChange={handlePageChange}
        showTags={true}
        showExport={true}
        consumption={consumption}
        orderLabels={
          <>
            {orderLabel("name", l("PAGES.SEARCH.ORDER.NAME"))}
            {orderLabel("sector", l("PAGES.SEARCH.ORDER.SECTOR"))}
            {orderLabel("city", l("PAGES.SEARCH.ORDER.CITY"))}
          </>
        }
      />

      <CreateTagModal
        visible={tagAddModal}
        setVisible={setAddTagModal}
        onCreate={() => reloadTags()}
      />

      <ContactSalesModal
        open={exportLimitedModal}
        dismiss={() => setExportLimitedModal(false)}
        mainMessage={l("ECOMMERCE.EXPORT_COMPANIES_LIMIT_TEXT")}
      />
    </>
  );
};

export default CompanySearch;
